import React, { useState } from "react";

import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import infoIcon2 from "../assets/icons/info_icon.svg";
import { Button } from "./ui/button";
import { setAntiFish } from "../services/settingsService";
import { useAuth } from "../context/AuthContext";
import { Eye } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { Loader2 } from "lucide-react";

// Feature flag - set to true when anti-phishing is ready for production
const ANTI_PHISHING_ENABLED = false;

export default function AntiPhishing({ setPageNo }) {
  const { settings, updateSettings } = useAuth();
  const { toast } = useToast();
  const [antiPhishing, setAntiPhishing] = useState(
    settings?.security?.antiPhishCode?.code || ""
  );
  const [inputType, setInputType] = useState("password");
  const [isLoading, setIsLoading] = useState(false);

  function toggleInput() {
    if (inputType === "password") {
      setInputType("text");
    } else {
      setInputType("password");
    }
  }

  async function submitAntiPhishing() {
    if (!ANTI_PHISHING_ENABLED) return;

    setIsLoading(true);
    try {
      const apiCall = await setAntiFish(antiPhishing);
      if (apiCall.status === 200 && apiCall.data.status === true) {
        const newSettings = apiCall.data.data;
        updateSettings(newSettings);
        setIsLoading(false);
        toast({
          description: "Anti Phishing Code Successfully Updated",
          variant: "eighty",
        });
        setPageNo(0);
      }
    } catch (err) {
      setIsLoading(false);
    }
  }

  return (
    <>
      <div className="flex justify-center mt-12 px-4 md:px-16 lg:px-32">
        <div className="max-w-3xl w-full">
          <Card className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <CardHeader className="px-9 pt-6 pb-4">
              <CardTitle className="text-2xl font-semibold text-text-secondary text-[20px]">
                <div className="flex items-center">
                  <div>Anti-phishing phrase</div>
                </div>
              </CardTitle>
            </CardHeader>

            <CardContent className="px-5 pt-2 pb-6">
              <div className="w-full px-4 mb-6 text-[16px] text-text-secondary">
                <p>
                  Configure an anti-phishing phrase to appear in all our emails,
                  so you can easily verify they're legitimate and stay protected
                  from phishing attempts.
                </p>
              </div>

              {/* Form section with frosted overlay when disabled */}
              <div className="relative">
                {/* Frosted overlay with Coming Soon - only shown when feature is disabled */}
                {!ANTI_PHISHING_ENABLED && (
                  <div className="absolute inset-0 z-10 backdrop-blur-[2px] bg-white/60 flex items-center justify-center rounded-xl">
                    <span className="bg-purple-600 text-white text-sm font-semibold px-4 py-2 rounded-full shadow-lg">
                      Coming Soon
                    </span>
                  </div>
                )}

                {/* Form content */}
                <div className="columns-1 mb-6">
                  <div className="relative w-full px-4">
                    <p className="text-[14px] text-text-secondary font-normal mb-2">
                      Anti-phishing phrase
                    </p>
                    <Eye
                      strokeWidth={1}
                      className="text-text-secondary absolute top-[50%] transform -translate-y-1/2 right-9 cursor-pointer"
                      onClick={() => {
                        if (ANTI_PHISHING_ENABLED) toggleInput();
                      }}
                    />
                    <input
                      type={inputType}
                      placeholder="Anti phishing phrase"
                      defaultValue={antiPhishing}
                      onChange={(e) => setAntiPhishing(e.target.value)}
                      disabled={!ANTI_PHISHING_ENABLED}
                      className={`border-[1px] rounded-[10px] border-bg-border text-sm w-full text-[14px] font-normal px-5 py-4 ${
                        !ANTI_PHISHING_ENABLED
                          ? "bg-gray-50 cursor-not-allowed"
                          : "bg-white focus:ring-2 focus:ring-purple-400 transition"
                      }`}
                    />
                    <div className="flex mt-2">
                      <img src={infoIcon2} alt="Info" />
                      <p className="ml-2 text-accent-pink text-[16px] font-semibold">
                        Tips for setting an anti-phishing phrase
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex justify-center">
                  <Button
                    disabled={!ANTI_PHISHING_ENABLED || antiPhishing === ""}
                    className={`bg-gradient-to-tr from-purple-500 to-rose-500 text-white py-2 rounded-full w-[50%] ${
                      !ANTI_PHISHING_ENABLED
                        ? "opacity-50 cursor-not-allowed"
                        : ""
                    }`}
                    onClick={() => {
                      submitAntiPhishing();
                    }}
                  >
                    {isLoading ? (
                      <Loader2 className="animate-spin text-white" />
                    ) : (
                      "Save"
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}
